import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { useAppDispatch } from '../../../../app/hooks'
import { uploadMultimediaAttachment } from '../../../../api/multimedia/multimediaApi'
import { addPersistedAttachmentFromExternalSource } from '../../../../redux/reducers/multimedia/multimediaFeature'
import { showMessage } from '../../../../redux/reducers/message/messageFeature'
import { AiUploadedImage, AiUploadImageSection } from './AiUploadImageSection'
import { AiUploadDocumentSection, AiUploadedDocument } from './AiUploadDocumentSection'
import { ProcessingState } from './AiDataExtractionAnalyzingModal'
import { AiDataExtractionAnalyzingModalMock } from './AiDataExtractionAnalyzingModalMock'

interface AiDataExtractionUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const MAX_IMAGE_FILE_SIZE_BYTES = 10 * 1024 * 1024
const MAX_DOCUMENT_FILE_SIZE_BYTES = 50 * 1024 * 1024

const getAttachmentTitleForMultimedia = (title: string | undefined, fallbackTitle: string) => {
  if (!title) {
    return fallbackTitle
  }

  const normalizedTitle = title.trim().toLowerCase()
  return normalizedTitle === 'undefined' || normalizedTitle === 'null' ? fallbackTitle : title
}

export const AiDataExtractionUploadModal = ({ isOpen, onClose, onSuccess }: AiDataExtractionUploadModalProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [images, setImages] = useState<AiUploadedImage[]>([])
  const [imageErrorText, setImageErrorText] = useState<string | undefined>()
  const [floorPlanDocument, setFloorPlanDocument] = useState<AiUploadedDocument | null>(null)
  const [floorPlanErrorText, setFloorPlanErrorText] = useState<string | undefined>()
  const [energyCertificateDocument, setEnergyCertificateDocument] = useState<AiUploadedDocument | null>(null)
  const [energyCertificateErrorText, setEnergyCertificateErrorText] = useState<string | undefined>()
  const imagesRef = useRef<AiUploadedImage[]>([])
  const floorPlanDocumentRef = useRef<AiUploadedDocument | null>(null)
  const energyCertificateDocumentRef = useRef<AiUploadedDocument | null>(null)

  useEffect(() => {
    imagesRef.current = images
  }, [images])

  useEffect(() => {
    floorPlanDocumentRef.current = floorPlanDocument
  }, [floorPlanDocument])

  useEffect(() => {
    energyCertificateDocumentRef.current = energyCertificateDocument
  }, [energyCertificateDocument])

  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl))
      revokeDocumentPreview(floorPlanDocumentRef.current)
      revokeDocumentPreview(energyCertificateDocumentRef.current)
    }
  }, [])

  const onImageSelect = (files: FileList | null) => {
    if (!files) {
      return
    }

    const selectedImages = Array.from(files).filter((file) => file.type.startsWith('image/'))
    const oversizedImageSelected = selectedImages.some((file) => file.size > MAX_IMAGE_FILE_SIZE_BYTES)

    setImageErrorText(oversizedImageSelected ? t('wizard.uploadAiBanner.modal.photos.maxFileSizeError') : undefined)

    const uploadedImages = selectedImages
      .filter((file) => file.size <= MAX_IMAGE_FILE_SIZE_BYTES)
      .map((file) => ({
        id: uuid(),
        name: file.name,
        file,
        previewUrl: URL.createObjectURL(file),
      }))

    if (uploadedImages.length > 0) {
      setImages((currentImages) => [...currentImages, ...uploadedImages])
      uploadedImages.forEach(async (image) => {
        try {
          const attachment = await uploadMultimediaAttachment(image.file)
          const attachmentTitle = getAttachmentTitleForMultimedia(attachment.title, image.name)
          dispatch(
            addPersistedAttachmentFromExternalSource({
              attachment: {
                ...attachment,
                title: attachmentTitle,
              },
              fallbackTitle: attachmentTitle,
            })
          )
        } catch (error) {
          dispatch(showMessage({ message: 'unknown' }))
        }
      })
    }
  }

  const onImageRemove = (imageId: string) => {
    const imageToRemove = imagesRef.current.find((image) => image.id === imageId)
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.previewUrl)
    }

    setImages((currentImages) => currentImages.filter((image) => image.id !== imageId))
  }

  const onImageReorder = (imageIdsInOrder: string[]) => {
    setImages((currentImages) => {
      const imagesById = new Map(currentImages.map((image) => [image.id, image]))
      return imageIdsInOrder
        .map((imageId) => imagesById.get(imageId))
        .filter((image): image is AiUploadedImage => Boolean(image))
    })
  }
  const [isAnalyzingOpen, setIsAnalyzingOpen] = useState(false)

  const mirrorAttachmentToMultimedia = async (file: File, options?: { isFloorPlan?: boolean }) => {
    try {
      const attachment = await uploadMultimediaAttachment(file, options?.isFloorPlan)
      const attachmentTitle = getAttachmentTitleForMultimedia(attachment.title, file.name)
      dispatch(
        addPersistedAttachmentFromExternalSource({
          attachment: {
            ...attachment,
            title: attachmentTitle,
          },
          fallbackTitle: attachmentTitle,
        })
      )
    } catch (error) {
      dispatch(showMessage({ message: 'unknown' }))
    }
  }

  const onFloorPlanSelect = (files: FileList | null) => {
    if (!files) {
      return
    }

    const file = Array.from(files).find((selectedFile) => {
      return selectedFile.type.startsWith('image/') || selectedFile.type === 'application/pdf'
    })

    if (!file) {
      return
    }

    if (file.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
      setFloorPlanErrorText(t('wizard.uploadAiBanner.modal.documents.maxFileSizeError'))
      return
    }

    revokeDocumentPreview(floorPlanDocumentRef.current)
    setFloorPlanErrorText(undefined)

    setFloorPlanDocument({
      id: uuid(),
      name: file.name,
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      uploadedAt: new Date(),
    })
    void mirrorAttachmentToMultimedia(file, { isFloorPlan: true })
  }

  const onEnergyCertificateSelect = (files: FileList | null) => {
    if (!files) {
      return
    }

    const file = Array.from(files).find((selectedFile) => selectedFile.type === 'application/pdf')

    if (!file) {
      return
    }

    if (file.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
      setEnergyCertificateErrorText(t('wizard.uploadAiBanner.modal.documents.maxFileSizeError'))
      return
    }

    setEnergyCertificateErrorText(undefined)
    setEnergyCertificateDocument({
      id: uuid(),
      name: file.name,
      file,
      uploadedAt: new Date(),
    })
    void mirrorAttachmentToMultimedia(file)
  }

  const onFloorPlanRemove = () => {
    revokeDocumentPreview(floorPlanDocumentRef.current)
    setFloorPlanDocument(null)
  }

  const onEnergyCertificateRemove = () => {
    revokeDocumentPreview(energyCertificateDocumentRef.current)
    setEnergyCertificateDocument(null)
  }

  const startAnalyzing = () => {
    setIsAnalyzingOpen(true)
    onClose()
  }

  const finishAnalyzing = () => {
    setIsAnalyzingOpen(false)
    onSuccess?.()
  }

  const primaryFooterBtn = (
    <Modal.FooterPrimaryBtn
      label={t('wizard.uploadAiBanner.modal.uploadButton')}
      onClick={startAnalyzing}
      variant='filledDefault'
    />
  )
  const secondaryFooterBtn = (
    <Modal.FooterSecondaryBtn label={t('wizard.uploadAiBanner.modal.cancelButton')} onClick={onClose} />
  )

  return (
    <>
      <Modal
        visible={isOpen && !isAnalyzingOpen}
        ariaLabelCloseButton='close'
        title={t('wizard.uploadAiBanner.modal.title')}
        onClose={onClose}
        secondaryFooterBtn={secondaryFooterBtn}
        primaryFooterBtn={primaryFooterBtn}
        width={601}
      >
        <p className='font-body-large-regular margin-bottom-l'>{t('wizard.uploadAiBanner.modal.description')}</p>
        <AiUploadImageSection
          errorText={imageErrorText}
          images={images}
          onFileSelect={onImageSelect}
          onRemove={onImageRemove}
          onReorder={onImageReorder}
        />
        <AiUploadDocumentSection
          energyCertificateErrorText={energyCertificateErrorText}
          floorPlanDocument={floorPlanDocument}
          floorPlanErrorText={floorPlanErrorText}
          energyCertificateDocument={energyCertificateDocument}
          onFloorPlanSelect={onFloorPlanSelect}
          onEnergyCertificateSelect={onEnergyCertificateSelect}
          onFloorPlanRemove={onFloorPlanRemove}
          onEnergyCertificateRemove={onEnergyCertificateRemove}
        />
        <p className='font-body-small-regular margin-bottom-l margin-top-m font-light'>
          <span className={'font-body-small-bold'}>{t('wizard.uploadAiBanner.modal.hint')}</span>
          <span>{t('wizard.uploadAiBanner.modal.hintText')}</span>
        </p>
      </Modal>
      <AiDataExtractionAnalyzingModalMock
        visible={isAnalyzingOpen}
        state={ProcessingState.Preparing}
        onFinished={finishAnalyzing}
      />
    </>
  )
}

const revokeDocumentPreview = (document: AiUploadedDocument | null) => {
  if (document?.previewUrl) {
    URL.revokeObjectURL(document.previewUrl)
  }
}
